import React, { useCallback, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Heading from '../../components/Heading';
import UploadBulk from './components/UploadBulk';
import { DraftContact } from 'models/Contact';
import ReorderResizeTable from 'components/ReorderResizeTable';
import {
  AddContactsPeopleTableKeys,
  CHECKBOX_COLUMNS,
  CUSTOM_COL_SIZES,
} from './components/ManageContactTableCells/data';
import { Link, useNavigate } from 'react-router-dom';
import { Loader } from 'ncoded-component-library';
import {
  BODY_CONTENT_GENERATE,
  HEADER_CONTENT_GENERATE,
  KeyCell,
  KeyHeader,
} from './components/ManageContactTableCells';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import ManageDraftContactDrawer from './components/ManageDraftContactDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import useBulkUploadReviewState from 'hooks/useBulkUploadReviewState';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import {
  checkIfCanProcessColumn,
  checkIfColumnIsValid,
  checkHasEveryRequiredColumn,
  checkAreAllRequiredFieldsValid,
  addMissingRequiredFieldsToContact,
  getInvalidContactsCount,
  getInvalidOptionalContactsCount,
  prepareValuesForApi,
} from './utils';
import BulkAploadActions from 'components/BulkAploadActions';
import { CONTACTS_ROUTE } from 'data/global';
import useCallbackAsync from 'hooks/useCallbackAsync';
import AddMessageForContactModal from './components/AddMessageForContactModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useMatchMedia from 'hooks/useMatchMedia';
import BulkUploadValidationBanner from 'router/subrouters/Dash/subrouters/private/subrouters/AdminDashboard/subrouters/Enterprises/subrouters/Enterprise/pages/ManagePeople/components/BulkUploadValidationBanner';

import './AddContacts.styles.scss';

type AddContactsProps = {
  className?: string;
};

const AddContacts: React.FC<AddContactsProps> = (props) => {
  const { className } = props;

  const [selectedContact, setSelectedContact] = useState<DraftContact>();

  const [manageContactDrawer, manageContactDrawerRef] =
    useCallbackRef<SideDrawerRef>();
  const [addMessageForContactModal, addMessageForContactModalRef] =
    useCallbackRef<ModalRef>();

  const classes = classNames('add-contacts', className);

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPhablet = useMatchMedia('isPhablet');

  const {
    columns,
    rows: contacts,
    setRows: setContacts,
    updateRow: updateContact,
    editRow: editContact,
    onUpdateColumn,
    handleUploadRows,
    clearRows,
  } = useBulkUploadReviewState<DraftContact>({
    priorityColumns: CHECKBOX_COLUMNS,
    addMissingRequiredFields: addMissingRequiredFieldsToContact,
  });

  const count = useMemo(() => contacts?.length, [contacts?.length]);

  const areAllContactApproved = useMemo(
    () => contacts.every(({ preApproved }) => preApproved),
    [contacts],
  );

  const numberOfProcessableColumns = useMemo(
    () => columns.filter(checkIfCanProcessColumn).length,
    [columns],
  );

  const numberOfUnProcessableColumns = useMemo(
    () => columns.length - numberOfProcessableColumns - CHECKBOX_COLUMNS.length,
    [columns, numberOfProcessableColumns],
  );

  const canSubmitContacts = useMemo(
    () =>
      !!count &&
      checkHasEveryRequiredColumn(columns) &&
      checkAreAllRequiredFieldsValid(contacts),
    [columns, contacts, count],
  );

  const invalidContactsCount = useMemo(
    () => getInvalidContactsCount(contacts),
    [contacts],
  );

  const invalidOptionalContactsCount = useMemo(
    () => getInvalidOptionalContactsCount(contacts),
    [contacts],
  );

  const onPreApproveContact = useCallback(
    ({ _id, preApproved }) => updateContact({ _id, preApproved: !preApproved }),
    [updateContact],
  );

  const onApproveAll = useCallback(
    () =>
      setContacts((old) =>
        old.map(({ preApproved, ...rest }) => ({
          ...rest,
          preApproved: !areAllContactApproved,
        })),
      ),
    [areAllContactApproved, setContacts],
  );

  const renderedContent = useMemo(
    () =>
      columns.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (contact: DraftContact) => {
            const { _id } = contact;

            const isPreApproved = contacts?.find(
              (el) => el._id === _id,
            )?.preApproved;

            if (checkIfColumnIsValid(col))
              return BODY_CONTENT_GENERATE[col as AddContactsPeopleTableKeys]({
                contact,
                isPreApproved,
                column: col,
                onPreApproveContact,
              });

            return <KeyCell column={col} contact={contact} />;
          },
        }),
        {},
      ),
    [columns, contacts, onPreApproveContact],
  );

  const renderedHeader = useMemo(
    () =>
      columns.reduce(
        (acc, col, index) => ({
          ...acc,
          [col]: () => {
            if (checkIfColumnIsValid(col))
              return HEADER_CONTENT_GENERATE[col as AddContactsPeopleTableKeys](
                {
                  column: col,
                  index,
                  allColumns: columns,
                  areAllContactApproved,
                  onApproveAll,
                  onUpdateColumn,
                },
              );

            return (
              <KeyHeader
                column={col}
                index={index}
                allColumns={columns}
                onUpdateColumn={onUpdateColumn}
              />
            );
          },
        }),
        {},
      ),
    [columns, areAllContactApproved, onApproveAll, onUpdateColumn],
  );

  const clearContacts = useCallback(() => {
    setSelectedContact(null);
    clearRows();
  }, [clearRows]);

  const handleEditContact = useCallback(
    (contact: DraftContact) => {
      setSelectedContact(contact);
      manageContactDrawer.setOpen(true);
    },
    [manageContactDrawer],
  );

  const handleDeleteContact = useCallback(
    (id: string) => setContacts((old) => old.filter((el) => el._id !== id)),
    [setContacts],
  );

  const [handleBulkUpload, isLoading] = useCallbackAsync(
    async (invitationMessage?: string) => {
      const {
        data: { numberOfContactsInvited },
      } = await api.contacts.bulkCreateContacts(
        prepareValuesForApi(contacts),
        (invitationMessage || '').trim() || undefined,
      );
      popSuccess(
        numberOfContactsInvited
          ? t('contactInvited', { count: numberOfContactsInvited })
          : t('contactsImportedSuccesfully', {
              count,
            }),
      );
      navigate('../');
    },
  );

  return (
    <div className={classes}>
      <Heading title={t('contacts')}>
        <Link to={CONTACTS_ROUTE}>
          {t(isPhablet ? 'contactsPage' : 'goToContactsPage')}
        </Link>
      </Heading>

      {isLoading && <Loader className="fixed" />}

      {!contacts.length ? (
        <UploadBulk onUpload={handleUploadRows} />
      ) : (
        <div className="white-panel">
          <h2>{t('bulkUploadContacts', { count })}</h2>

          <BulkUploadValidationBanner
            invalidMembersCount={invalidContactsCount}
            invalidOptionalMembersCount={invalidOptionalContactsCount}
          />

          <ReorderResizeTable
            columns={columns}
            isDragDisabled
            isResizeDisabled
            hasHorizontalScrollFade={false}
            data={contacts}
            customSizes={CUSTOM_COL_SIZES}
            tableContentTransformations={renderedContent}
            tableHeaderContentTransformations={renderedHeader}
            onRowClick={handleEditContact}
          />

          <BulkAploadActions
            title={t('uploadContacts', { count })}
            isDisabled={!canSubmitContacts}
            numberOfProcessableColumns={numberOfProcessableColumns}
            numberOfUnProcessableColumns={numberOfUnProcessableColumns}
            onClear={clearContacts}
            handleBulkUpload={addMessageForContactModal?.open}
          />

          <AddMessageForContactModal
            ref={addMessageForContactModalRef}
            onSubmit={handleBulkUpload}
          />

          <ManageDraftContactDrawer
            contact={selectedContact}
            ref={manageContactDrawerRef}
            onSuccess={editContact}
            onDelete={handleDeleteContact}
            onClose={() => setSelectedContact(null)}
          />
        </div>
      )}
    </div>
  );
};

export default AddContacts;
